import {
    IPreviewInfo,
    IPreviewProblem,
    IPreviewTarget,
    ITextPreview,
    PreviewErrorCode,
    IPreviewOrigin,
} from './filePreview.types';

/** Отказ предпросмотра с машинным кодом — по нему выбираются текст и действия на экране. */
export class PreviewError extends Error {
    constructor(
        readonly code: PreviewErrorCode,
        message: string,
    ) {
        super(message);
        this.name = 'PreviewError';
    }
}

/** Ход скачивания: `total` нет, если сервер не назвал размер. */
export interface IDownloadProgress {
    loaded: number;
    total?: number;
}

export interface IPreviewDownloadOptions {
    onProgress?: (progress: IDownloadProgress) => void;
}

/** Откуда окно берёт файлы. Обычно — `httpPreviewSource` поверх HTTP-клиента приложения; в stories и тестах — подмена. */
export interface IFilePreviewSource {
    info: (target: IPreviewTarget, signal: AbortSignal) => Promise<IPreviewInfo>;
    /** PDF, книга или исходные байты. `version` делает адрес неизменяемым — повторное открытие берётся из кеша браузера. */
    bytes: (
        target: IPreviewTarget,
        rendition: 'pdf' | 'sheet' | 'content',
        version: string,
        signal: AbortSignal,
    ) => Promise<ArrayBuffer>;
    text: (target: IPreviewTarget, signal: AbortSignal) => Promise<ITextPreview>;
    /** Скачивание оригинала: файла или записи архива. */
    download: (target: IPreviewTarget, options?: IPreviewDownloadOptions) => Promise<void>;
}

/** Запрос окна: любой статус, кроме 401, — ответом, а не исключением, и без общего тоста — отказ предпросмотра несёт свой
 *  код (`{ code, message }`), а 502–504 здесь — не падение стенда, а занятый конвертер. 401 — перехватчику клиента. */
interface IPreviewGetConfig {
    params: Record<string, string | number>;
    responseType: 'json' | 'arraybuffer';
    signal: AbortSignal;
    suppressErrorNotification: true;
    validateStatus: (status: number) => boolean;
}

/** HTTP-клиент приложения (`@/http`): базовый адрес API, Bearer и обновление токена — его забота. */
export interface IFilePreviewHttp {
    client: { get: (url: string, config: IPreviewGetConfig) => Promise<{ status: number; data: unknown }> };
    /** Скачивание файла по адресу API (имя — из `Content-Disposition`). */
    download: (url: string, options?: IPreviewDownloadOptions) => Promise<void>;
}

const notUnauthorized = (status: number): boolean => status !== 401;

const baseParams = (target: IPreviewTarget): Record<string, string | number> => ({
    ...target.origin.params,
    ...(target.entry ? { entry: target.entry } : {}),
});

const query = (params: Record<string, string | number>): string =>
    new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString();

const toPreviewError = (status: number, body: unknown): PreviewError => {
    const problem = parseProblem(body);
    if (problem) return new PreviewError(problem.code, problem.message);
    if (status === 403) return new PreviewError('forbidden', 'Нет прав на просмотр этого файла.');
    if (status === 404) return new PreviewError('notFound', 'Файл не найден — возможно, его удалили.');
    return new PreviewError('unavailable', `Сервер не смог подготовить предпросмотр (код ${status}).`);
};

const parseProblem = (body: unknown): IPreviewProblem | undefined => {
    let data = body;
    // Бинарные запросы получают тело отказа тоже ArrayBuffer; проверка по тегу — не instanceof,
    // чтобы не зависеть от окна/realm, где буфер создан.
    if (Object.prototype.toString.call(data) === '[object ArrayBuffer]') {
        try {
            data = JSON.parse(new TextDecoder().decode(data as ArrayBuffer));
        } catch {
            return undefined;
        }
    }
    if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
        const { code, message } = data as IPreviewProblem;
        if (typeof code === 'string' && typeof message === 'string') return { code, message };
    }
    return undefined;
};

/**
 * Предпросмотр по протоколу `FilePreviewControllerBase` сервера: `{origin.path}/info|pdf|sheet|text|content`
 * с параметрами `origin.params` и записью архива `entry`.
 */
export const httpPreviewSource = (http: IFilePreviewHttp): IFilePreviewSource => {
    const request = async <T>(
        target: IPreviewTarget,
        action: string,
        responseType: 'json' | 'arraybuffer',
        signal: AbortSignal,
        extra?: Record<string, string>,
    ): Promise<T> => {
        let response: { status: number; data: unknown };
        try {
            response = await http.client.get(`/${target.origin.path}/${action}`, {
                params: { ...baseParams(target), ...extra },
                responseType,
                signal,
                suppressErrorNotification: true,
                validateStatus: notUnauthorized,
            });
        } catch (error) {
            if (signal.aborted) throw error;
            throw new PreviewError('network', 'Нет связи с сервером. Проверьте соединение и повторите попытку.');
        }

        if (response.status >= 400) throw toPreviewError(response.status, response.data);
        return response.data as T;
    };

    return {
        info: (target, signal) => request<IPreviewInfo>(target, 'info', 'json', signal),
        bytes: (target, rendition, version, signal) =>
            request<ArrayBuffer>(target, rendition, 'arraybuffer', signal, { v: version }),
        text: (target, signal) => request<ITextPreview>(target, 'text', 'json', signal),
        download: (target, options) => http.download(downloadUrl(target.origin, target.entry), options),
    };
};

/** Оригинал файла — своим адресом источника, если он есть; запись архива и файл без него — байтами `content`. */
const downloadUrl = (origin: IPreviewOrigin, entry?: string): string =>
    origin.download && !entry
        ? origin.download
        : `${origin.path}/content?${query({ ...baseParams({ origin, entry }), download: 'true' })}`;
